<template>
  <div class="match-page">
    <el-row :gutter="16" class="stats-row">
      <el-col v-for="card in topStatCards" :key="card.key" :xs="24" :sm="12" :lg="6">
        <el-card shadow="hover" class="stat-card" :body-style="{ padding: '20px' }">
          <div class="stat-top">
            <div>
              <div class="stat-label">{{ card.label }}</div>
              <div class="stat-value">{{ card.value }}</div>
              <div class="stat-extra" :class="card.extraClass">{{ card.extra }}</div>
            </div>
            <div class="stat-icon" :style="{ background: card.bg }">
              <i :class="card.icon" />
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="sub-stats-row">
      <el-col v-for="card in matchStatCards" :key="card.key" :xs="24" :sm="8">
        <div class="sub-stat-card" :class="card.cls">
          <i :class="card.icon" />
          <div>
            <div class="sub-stat-label">{{ card.label }}</div>
            <div class="sub-stat-value">{{ card.value }}</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <el-card shadow="never" class="table-card">
      <div slot="header" class="card-header">
        <span>撮合记录</span>
        <div class="filters">
          <el-input
            v-model="keyword"
            placeholder="搜索需求标题、发布人、创作者..."
            clearable
            class="toolbar-search"
            @keyup.enter.native="handleSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch" />
          </el-input>
          <el-select v-model="filterMatchStatus" placeholder="全部状态" clearable @change="handleSearch">
            <el-option v-for="opt in matchOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </div>
      </div>

      <el-table v-loading="loading" :data="list" stripe class="match-table">
        <el-table-column prop="demandTitle" label="需求标题" min-width="180" show-overflow-tooltip />
        <el-table-column label="发布人" width="130">
          <template slot-scope="{ row }">{{ row.publisherName || '-' }}</template>
        </el-table-column>
        <el-table-column label="对接创作者" width="140">
          <template slot-scope="{ row }">{{ row.creatorName || `用户${row.creatorUserId}` }}</template>
        </el-table-column>
        <el-table-column label="撮合状态" width="110">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="matchStatusTagType(row.matchStatus)">
              {{ labelOf(MATCH_STATUS, row.matchStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="撮合时间" width="120">
          <template slot-scope="{ row }">{{ formatUserDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="row.matchStatus === 0"
              type="text"
              size="small"
              @click="openMatchDialog(row)"
            >处理</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadList"
      />
    </el-card>

    <el-dialog title="撮合详情" :visible.sync="detailVisible" width="480px">
      <el-descriptions v-if="detail" :column="1" border size="small">
        <el-descriptions-item label="需求标题">{{ detail.demandTitle }}</el-descriptions-item>
        <el-descriptions-item label="发布人">{{ detail.publisherName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="对接创作者">{{ detail.creatorName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="撮合状态">{{ labelOf(MATCH_STATUS, detail.matchStatus) }}</el-descriptions-item>
        <el-descriptions-item label="撮合时间">{{ formatUserDate(detail.createdAt) }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <el-dialog title="处理撮合" :visible.sync="matchVisible" width="420px">
      <el-form ref="matchForm" :model="matchForm" :rules="matchRules" label-width="90px">
        <el-form-item label="撮合状态" prop="matchStatus">
          <el-select v-model="matchForm.matchStatus" style="width: 100%">
            <el-option v-for="opt in matchOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="matchVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitMatch">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDemandStats, getMatchRecords, getMatchStats, matchDemand } from '@/api/admin'
import { MATCH_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import { formatCount } from '@/utils/dashboardFormat'
import { formatDemandAmount } from '@/utils/demandFormat'
import { formatUserDate } from '@/utils/userFormat'

export default {
  name: 'DemandsMatch',
  data() {
    return {
      MATCH_STATUS,
      loading: false,
      submitting: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      keyword: '',
      filterMatchStatus: null,
      demandStats: {},
      matchStats: {},
      detailVisible: false,
      detail: null,
      matchVisible: false,
      matchForm: { demandId: null, userId: null, matchStatus: 1 },
      matchRules: {
        matchStatus: [{ required: true, message: '请选择撮合状态', trigger: 'change' }]
      }
    }
  },
  computed: {
    matchOptions() {
      return optionsOf(MATCH_STATUS)
    },
    topStatCards() {
      return [
        {
          key: 'total',
          label: '总发布需求',
          value: formatCount(this.demandStats.totalCount),
          extra: `↑ 本月新增 +${formatCount(this.demandStats.monthNewCount)}`,
          extraClass: 'up',
          icon: 'el-icon-document',
          bg: 'rgba(64, 158, 255, 0.12)'
        },
        {
          key: 'pending',
          label: '待审核',
          value: formatCount(this.demandStats.pendingCount),
          extra: '需尽快处理',
          extraClass: 'warn',
          icon: 'el-icon-warning-outline',
          bg: 'rgba(245, 158, 11, 0.12)'
        },
        {
          key: 'matching',
          label: '撮合中',
          value: formatCount(this.demandStats.matchingCount),
          extra: '等待对接',
          extraClass: '',
          icon: 'el-icon-connection',
          bg: 'rgba(139, 92, 246, 0.12)'
        },
        {
          key: 'amount',
          label: '需求成交额',
          value: formatDemandAmount(this.demandStats.successAmount),
          extra: '累计成交金额',
          extraClass: '',
          icon: 'el-icon-money',
          bg: 'rgba(16, 185, 129, 0.12)'
        }
      ]
    },
    matchStatCards() {
      return [
        {
          key: 'matching',
          label: '撮合中',
          value: formatCount(this.matchStats.matchingCount),
          icon: 'el-icon-user',
          cls: 'sub-blue'
        },
        {
          key: 'success',
          label: '已撮合成功',
          value: formatCount(this.matchStats.successCount),
          icon: 'el-icon-connection',
          cls: 'sub-green'
        },
        {
          key: 'rejected',
          label: '已拒绝',
          value: formatCount(this.matchStats.rejectedCount),
          icon: 'el-icon-close',
          cls: 'sub-red'
        }
      ]
    }
  },
  created() {
    this.loadStats()
    this.loadList()
  },
  methods: {
    labelOf,
    formatUserDate,
    matchStatusTagType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'primary'
    },
    handleSearch() {
      this.page = 1
      this.loadList()
    },
    async loadStats() {
      try {
        const [demandStats, matchStats] = await Promise.all([getDemandStats(), getMatchStats()])
        this.demandStats = demandStats || {}
        this.matchStats = matchStats || {}
      } catch (e) {
      }
    },
    async loadList() {
      this.loading = true
      try {
        const result = await getMatchRecords({
          page: this.page,
          size: this.pageSize,
          keyword: this.keyword || undefined,
          matchStatus: this.filterMatchStatus != null ? this.filterMatchStatus : undefined
        })
        this.list = result.list || []
        this.total = result.total || 0
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    openDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    },
    openMatchDialog(row) {
      this.matchForm = { demandId: row.demandId, userId: row.creatorUserId, matchStatus: 1 }
      this.matchVisible = true
    },
    submitMatch() {
      this.$refs.matchForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await matchDemand(this.matchForm.demandId, {
            userId: this.matchForm.userId,
            matchStatus: this.matchForm.matchStatus
          })
          this.$message.success('处理成功')
          this.matchVisible = false
          this.loadStats()
          this.loadList()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style scoped>
.stats-row {
  margin-bottom: 16px;
}
.sub-stats-row {
  margin-bottom: 16px;
}
.stat-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.stat-label {
  color: #909399;
  font-size: 13px;
}
.stat-value {
  font-size: 28px;
  font-weight: 600;
  margin: 8px 0 4px;
  color: #303133;
}
.stat-extra {
  font-size: 12px;
  color: #909399;
}
.stat-extra.up {
  color: #67c23a;
}
.stat-extra.warn {
  color: #e6a23c;
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #606266;
}
.sub-stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.sub-stat-card i {
  font-size: 28px;
}
.sub-blue {
  background: rgba(64, 158, 255, 0.08);
  color: #409eff;
}
.sub-green {
  background: rgba(103, 194, 58, 0.08);
  color: #67c23a;
}
.sub-red {
  background: rgba(245, 108, 108, 0.08);
  color: #f56c6c;
}
.sub-stat-label {
  font-size: 13px;
  color: #909399;
}
.sub-stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-top: 4px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.toolbar-search {
  width: 280px;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
</style>
